import type {
  AccountGradingRecording,
  CustomerInteractionAgentStep,
  CustomerInteractionText,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { DateTime } from "luxon";

/** A text, or a step the agent took between texts. */
export type GradingTranscriptThreadEntry =
  | { type: "text"; text: CustomerInteractionText }
  | { type: "agent_step"; step: CustomerInteractionAgentStep };

/** The text or step an entry holds; both carry their event's id and time. */
export function resolveThreadEntryEvent(entry: GradingTranscriptThreadEntry) {
  return entry.type === "text" ? entry.text : entry.step;
}

/** A run between calls containing at least one text, with its agent activity. */
export interface GradingTranscriptTextConversationItem {
  type: "texts";
  entries: readonly GradingTranscriptThreadEntry[];
  firstText: CustomerInteractionText;
  messageCount: number;
}

/** Calls, genuine text conversations, and standalone agent activity. */
export type GradingTranscriptItem =
  | { type: "call"; recording: AccountGradingRecording }
  | GradingTranscriptTextConversationItem
  | { type: "agent_activity"; step: CustomerInteractionAgentStep };

/**
 * An interaction's calls, texts and agent steps in the order they happened,
 * with each run of the thread between two calls kept together. All arrive
 * oldest first.
 */
export function buildGradingTranscriptItems(
  recordings: readonly AccountGradingRecording[],
  texts: readonly CustomerInteractionText[],
  agentSteps: readonly CustomerInteractionAgentStep[],
): GradingTranscriptItem[] {
  const threadTimeline = [
    ...texts.map((text): GradingTranscriptThreadEntry => ({
      type: "text",
      text,
    })),
    ...agentSteps.map((step): GradingTranscriptThreadEntry => ({
      type: "agent_step",
      step,
    })),
  ].toSorted(
    (left, right) =>
      DateTime.fromISO(resolveThreadEntryEvent(left).at).toMillis() -
      DateTime.fromISO(resolveThreadEntryEvent(right).at).toMillis(),
  );
  const items: GradingTranscriptItem[] = [];
  let currentThreadSegment: GradingTranscriptThreadEntry[] = [];
  let callIndex = 0;

  const appendThreadSegment = () => {
    const segmentTexts = currentThreadSegment.filter(
      (entry) => entry.type === "text",
    );
    const firstText = segmentTexts.at(0)?.text;
    if (firstText) {
      items.push({
        type: "texts",
        entries: currentThreadSegment,
        firstText,
        messageCount: segmentTexts.length,
      });
    } else {
      for (const entry of currentThreadSegment) {
        if (entry.type === "agent_step") {
          items.push({ type: "agent_activity", step: entry.step });
        }
      }
    }
    currentThreadSegment = [];
  };

  for (const entry of threadTimeline) {
    const entryAt = DateTime.fromISO(
      resolveThreadEntryEvent(entry).at,
    ).toMillis();
    let nextCall = recordings[callIndex];
    while (
      nextCall &&
      DateTime.fromISO(nextCall.recorded_at).toMillis() <= entryAt
    ) {
      appendThreadSegment();
      items.push({ type: "call", recording: nextCall });
      callIndex += 1;
      nextCall = recordings[callIndex];
    }

    currentThreadSegment.push(entry);
  }

  appendThreadSegment();
  for (const recording of recordings.slice(callIndex)) {
    items.push({ type: "call", recording });
  }

  return items;
}
